import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Globe, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Business } from '@/types/homepage';
import { MapLibreMap } from '@/components/map/MapLibreMap';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useHomePageStore from '@/store/useHomePageStore';
import { useHomePage } from '../HomePageContext';
import { MobileFullscreenControls } from './MobileFullscreenControls';
import { MobileBusinessOverlay } from './MobileBusinessOverlay';
import { FilterModal } from './FilterModal';
import { DesktopSidebar } from './DesktopSidebar';
import { MapLayoutProps } from './types';
import { MapLoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import '@/styles/custom-scrollbar.css';

export function ResponsiveMapLayout({
  businesses,
  visibleBusinesses = businesses,
  isSearchInPlace = true,
  isLoading = false,
  selectedBusiness: propSelectedBusiness,
  onBusinessSelect: propOnBusinessSelect,
  mapComponent,
  listComponent,
  className,
  onBoundsChange,
  onMapReady,
  externalMapStatus,
  onSearch,
  isMobileMapOnly = false
}: MapLayoutProps) {
  const { t } = useTranslation('public', { keyPrefix: 'homepage' });

  // Store and context
  const storeDrawerHeight = useHomePageStore(state => state.drawerHeight);
  const setStoreDrawerHeight = useHomePageStore(state => state.setDrawerHeight);
  const businessActivities = useHomePageStore(state => state.businessActivities);
  const setSearchQuery = useHomePageStore(state => state.setSearchQuery);
  const mapRef = useHomePageStore(state => state.mapRef);
  const { preloadCustomAdventuresForBusinesses, businessesWithCustomAdventures } = useHomePage();

  // Media query
  const isMobile = useMediaQuery('(max-width: 768px)');

  // State management
  const [internalSelectedBusiness, setInternalSelectedBusiness] = useState<Business | null>(null);
  const [showMobileBusinessCard, setShowMobileBusinessCard] = useState(false);
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
  const [showFloatingSearch, setShowFloatingSearch] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showAllPins, setShowAllPins] = useState(false);

  // Filter state from store
  const {
    activityFilters,
    selectedAnimalTypes,
    selectedAdventureTypes,
    additionalFilters,
    setActivityFilters,
    setSelectedAnimalTypes,
    setSelectedAdventureTypes,
    setSelectedSeason,
    setAdditionalFilters,
  } = useHomePageStore();

  // Refs
  const overlayRef = useRef<HTMLDivElement>(null);

  // Business selection logic
  const selectedBusiness = propSelectedBusiness || internalSelectedBusiness;

  // On desktop, let the wheel scroll the page until the section is in place
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (isSearchInPlace || isMobile) {
      map.scrollZoom.enable();
    } else {
      map.scrollZoom.disable();
    }
  }, [mapRef, isSearchInPlace, isMobile]);

  // Preload custom adventures
  useEffect(() => {
    if (businesses.length > 0) {
      const businessIds = businesses.map(b => b._id);
      preloadCustomAdventuresForBusinesses(businessIds);
    }
  }, [businesses, preloadCustomAdventuresForBusinesses]);

  // Business selection handler
  const handleBusinessSelect = useCallback((business: Business) => {
    if (propOnBusinessSelect) {
      propOnBusinessSelect(business);
    } else {
      setInternalSelectedBusiness(business);
    }
    
    // Show mobile popup on business selection
    if (isMobile) {
      setShowMobileBusinessCard(true);
    }
  }, [propOnBusinessSelect, isMobile]);

  // Mobile business card close handler
  const handleCloseMobileBusinessCard = useCallback(() => {
    setShowMobileBusinessCard(false);
    
    if (propOnBusinessSelect) {
      propOnBusinessSelect(null);
    } else {
      setInternalSelectedBusiness(null);
    }
  }, [propOnBusinessSelect]);

  // Mobile overlay click handler
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      handleCloseMobileBusinessCard();
    }
  }, [handleCloseMobileBusinessCard]);

  // Mobile fullscreen handlers
  const handleMobileFullscreenToggle = useCallback(() => {
    setIsMobileFullscreen(prev => {
      const newFullscreen = !prev;
      if (newFullscreen) {
        setShowFloatingSearch(true);
        setShowMobileBusinessCard(false);
      } else {
        setShowFloatingSearch(false);
        setShowFilterModal(false);
      }
      return newFullscreen;
    });
  }, []);

  const handleFloatingSearchToggle = useCallback(() => {
    setShowFloatingSearch(prev => !prev);
    if (showFilterModal) {
      setShowFilterModal(false);
    }
  }, [showFilterModal]);

  const handleFloatingFiltersToggle = useCallback(() => {
    setShowFilterModal(true);
    if (showFloatingSearch) {
      setShowFloatingSearch(false);
    }
  }, [showFloatingSearch]);

  const handleCloseSearch = useCallback(() => {
    setShowFloatingSearch(false);
  }, []);

  // Zoom out until the whole world fits; the list follows the map's view
  const handleWorldView = useCallback(() => {
    const map = useHomePageStore.getState().mapRef.current;
    if (!map) return;

    const worldZoom = Math.max(0, Math.log2(map.getContainer().clientWidth / 512));
    setSearchQuery('');
    map.flyTo({ center: [10, 25], zoom: worldZoom, duration: 1200, essential: true });
  }, [setSearchQuery]);

  // Filter management
  const handleClearFilters = useCallback(() => {
    setActivityFilters({
      showAnimalActivities: true,
      showAdventureActivities: true
    });
    setSelectedAnimalTypes([]);
    setSelectedAdventureTypes([]);
    setSelectedSeason(null);
    setAdditionalFilters({
      ...additionalFilters,
      groupSize: [1, 20]
    });
  }, [setActivityFilters, setSelectedAnimalTypes, setSelectedAdventureTypes, setSelectedSeason, setAdditionalFilters, additionalFilters]);

  // Only count filters that actually affect results (exclude Season and Group size until backend supports them)
  const hasActiveFilters = useMemo(() =>
    selectedAnimalTypes.length > 0 ||
    selectedAdventureTypes.length > 0 ||
    (!activityFilters.showAnimalActivities || !activityFilters.showAdventureActivities)
  , [selectedAnimalTypes, selectedAdventureTypes, activityFilters]);


  return (
    <div className={cn("flex flex-col md:flex-row relative", className)}>
      {/* Desktop Sidebar */}
      <DesktopSidebar
        listComponent={listComponent}
        isScrollable={isSearchInPlace}
      />

      {/* Map View */}
      <div className={cn(
        "relative isolate h-[45svh] min-h-[280px] md:h-auto md:flex-1 md:min-h-[400px]",
        isMobile && isMobileFullscreen && "fixed inset-0 z-50 h-dvh min-h-0 w-screen",
        isMobile && isMobileMapOnly && "w-full"
      )}>
        {mapComponent || (
          <MapLibreMap
            businesses={businesses}
            selectedBusiness={selectedBusiness}
            onBusinessSelect={handleBusinessSelect}
            onBoundsChange={onBoundsChange}
            onMapReady={onMapReady}
            height={isMobile && (isMobileFullscreen || isMobileMapOnly) ? "100dvh" : "100%"}
            externalMapStatus={externalMapStatus}
            clusterMarkers={!showAllPins}
          />
        )}

        {/* World view and clustering controls */}
        <div className="absolute left-3 bottom-6 md:bottom-auto md:top-3 z-[45] flex gap-2">
          <button
            type="button"
            onClick={handleWorldView}
            aria-label={t('map.worldView')}
            className="flex items-center justify-center gap-2 h-11 min-w-11 md:h-9 md:px-3 rounded-lg border border-primary bg-white text-primary text-sm font-semibold shadow-md hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <Globe className="h-4 w-4 shrink-0" />
            <span className="hidden md:inline">{t('map.worldView')}</span>
          </button>
          <button
            type="button"
            aria-pressed={showAllPins}
            aria-label={t('map.showAllPins')}
            onClick={() => setShowAllPins(prev => !prev)}
            className={cn(
              "flex items-center justify-center gap-2 h-11 min-w-11 md:h-9 md:px-3 rounded-lg border border-primary text-sm font-semibold shadow-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              showAllPins ? "bg-primary text-primary-foreground" : "bg-white text-primary hover:bg-secondary"
            )}
          >
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="hidden md:inline">{t('map.showAllPins')}</span>
          </button>
        </div>
        
        {/* Map Loading Overlay */}
        {isLoading && externalMapStatus?.state === 'searching' && (
          <MapLoadingSpinner />
        )}

        {/* Mobile Fullscreen Controls */}
        <MobileFullscreenControls
          isMobileFullscreen={isMobileFullscreen}
          showFloatingSearch={showFloatingSearch}
          hasActiveFilters={hasActiveFilters}
          onFullscreenToggle={handleMobileFullscreenToggle}
          onSearchToggle={handleFloatingSearchToggle}
          onFilterToggle={handleFloatingFiltersToggle}
          onSearch={onSearch}
          onCloseSearch={handleCloseSearch}
        />
      </div>

      {/* Results list under the map on phones */}
      {isMobile && <div className="mt-2">{listComponent}</div>}

      {/* Mobile Business Overlay */}
      <MobileBusinessOverlay
        selectedBusiness={selectedBusiness}
        showOverlay={showMobileBusinessCard}
        businessActivities={businessActivities}
        businessesWithCustomAdventures={businessesWithCustomAdventures}
        onClose={handleCloseMobileBusinessCard}
        onOverlayClick={handleOverlayClick}
      />

      {/* Filter Modal */}
      <FilterModal
        isOpen={showFilterModal}
        hasActiveFilters={hasActiveFilters}
        onClose={() => setShowFilterModal(false)}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
} 